package com.schoolconnect.app.feature.schoolusers.edit

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogFullNameField
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogMobileField
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.feature.schoolusers.SchoolUserSchoolPopup
import com.schoolconnect.app.feature.schoolusers.add.AddSchoolUserSchoolOption
import com.schoolconnect.app.feature.schoolusers.schoolUserGenderLabels
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.pencil
import ischoolconnect.sharedui.generated.resources.search
import org.jetbrains.compose.resources.painterResource

private val EditSchoolUserPrimary = com.schoolconnect.app.designsystem.AppPrimary
private val EditSchoolUserNavy = com.schoolconnect.app.designsystem.AppNavy
private val EditSchoolUserMuted = Color(0xFF536080)
private val EditSchoolUserBorder = com.schoolconnect.app.designsystem.AppBorder
private val EditSchoolUserInputBorder = com.schoolconnect.app.designsystem.AppInputBorder
private val EditSchoolUserDanger = com.schoolconnect.app.designsystem.AppDanger
private val EditSchoolUserSurface = com.schoolconnect.app.designsystem.AppSurface
private val EditSchoolUserDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


@Composable
internal fun EditSchoolUserModal(
    open: Boolean,
    initialValues: EditSchoolUserFormValues,
    schoolOptions: List<AddSchoolUserSchoolOption> = editSchoolUserSchools,
    onClose: () -> Unit,
    onSave: (EditSchoolUserFormValues) -> Unit,
    isSaving: Boolean = false,
    apiError: String = ""
) {
    if (!open) return

    var fullName by remember(initialValues) { mutableStateOf(initialValues.fullName) }
    var staffId by remember(initialValues) { mutableStateOf(initialValues.staffId) }
    var staffRole by remember(initialValues) { mutableStateOf(initialValues.staffRole) }
    var mobileNumber by remember(initialValues) { mutableStateOf(initialValues.mobileNumber) }
    var emailAddress by remember(initialValues) { mutableStateOf(initialValues.emailAddress) }
    var schoolName by remember(initialValues) { mutableStateOf(initialValues.schoolName) }
    var schoolId by remember(initialValues) { mutableStateOf(initialValues.schoolId) }
    var role by remember(initialValues) { mutableStateOf(initialValues.role) }
    var gender by remember(initialValues) { mutableStateOf(initialValues.gender) }
    var status by remember(initialValues) { mutableStateOf(initialValues.status) }
    var hasSubmitted by remember(initialValues) { mutableStateOf(true) }
    var schoolQuery by remember(initialValues) { mutableStateOf("") }
    var schoolExpanded by remember { mutableStateOf(false) }
    var roleExpanded by remember { mutableStateOf(false) }
    var genderExpanded by remember { mutableStateOf(false) }
    var genderOptions by remember { mutableStateOf(emptyList<String>()) }
    var statusExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        genderOptions = schoolUserGenderLabels()
    }

    val validationErrors = remember(
        hasSubmitted,
        staffId,
        fullName,
        staffRole,
        mobileNumber,
        emailAddress,
        schoolName,
        schoolId,
        role,
        gender,
        status
    ) {
        if (hasSubmitted) {
            validateEditSchoolUser(
                EditSchoolUserFormValues(
                    staffId = staffId,
                    fullName = fullName,
                    staffRole = staffRole,
                    mobileNumber = mobileNumber,
                    emailAddress = emailAddress,
                    schoolName = schoolName,
                    schoolId = schoolId,
                    role = role,
                    gender = gender,
                    status = status
                )
            )
        } else {
            EditSchoolUserValidationErrors()
        }
    }

    val currentValues = EditSchoolUserFormValues(
        staffId = staffId,
        fullName = fullName,
        staffRole = staffRole,
        mobileNumber = mobileNumber,
        emailAddress = emailAddress,
        schoolName = schoolName,
        schoolId = schoolId,
        role = role,
        gender = gender,
        status = status
    )
    val canSave = !isSaving
    val resolvedSchoolOptions = schoolOptions.ifEmpty { editSchoolUserSchools }
    val selectedSchool = resolvedSchoolOptions.firstOrNull { it.id == schoolId }
    val selectedSchoolLabel = selectedSchool?.name ?: schoolName
    val filteredSchools = remember(schoolQuery, resolvedSchoolOptions) {
        val query = schoolQuery.trim()
        if (query.isBlank()) {
            resolvedSchoolOptions
        } else {
            resolvedSchoolOptions.filter { it.name.contains(query, ignoreCase = true) }
        }
    }

    LaunchedEffect(initialValues) {
        fullName = initialValues.fullName
        staffId = initialValues.staffId
        staffRole = initialValues.staffRole
        mobileNumber = initialValues.mobileNumber
        emailAddress = initialValues.emailAddress
        schoolName = initialValues.schoolName
        schoolId = initialValues.schoolId
        role = initialValues.role
        gender = initialValues.gender
        status = initialValues.status
        hasSubmitted = true
        schoolQuery = ""
        schoolExpanded = false
        roleExpanded = false
        genderExpanded = false
        statusExpanded = false
    }

    AppDialogScaffold(
        onDismiss = { if (!isSaving) onClose() },
        dismissOnClickOutside = false,
        colors = EditSchoolUserDialogColors,
        isLoading = isSaving,
        loadingTitle = "Saving school user"
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AppDialogHeader(
                title = "Edit School User",
                subtitle = "Update school user information and access.",
                icon = Res.drawable.pencil,
                closeContentDescription = "Close edit school user",
                onClose = { if (!isSaving) onClose() },
                colors = EditSchoolUserDialogColors
            )
            AppDialogDivider(colors = EditSchoolUserDialogColors)
            AppDialogValidationSummary(
                errors = listOf(
                    validationErrors.staffId,
                    validationErrors.fullName,
                    validationErrors.staffRole,
                    validationErrors.mobileNumber,
                    validationErrors.emailAddress,
                    validationErrors.schoolId,
                    validationErrors.role,
                    validationErrors.gender,
                    validationErrors.status
                ),
                colors = EditSchoolUserDialogColors,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )
            AppDialogErrorMessage(
                message = apiError,
                colors = EditSchoolUserDialogColors,
                modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
            )

            AppDialogFormGrid(
                modifier = Modifier
                    .fillMaxWidth()
                    // Keep the footer in the dialog's measured bounds. The edit form
                    // owns its scrolling, so it must not consume the footer's space.
                    .weight(1f, fill = false),
                compactBreakpoint = 680.dp
            ) {
                val compact = it
                val scrollState = rememberScrollState()
                val verticalGap = if (compact) 20.dp else 28.dp

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(scrollState)
                        .padding(horizontal = 28.dp, vertical = 24.dp),
                    verticalArrangement = Arrangement.spacedBy(verticalGap)
                ) {
                        if (compact) {
                            Column(verticalArrangement = Arrangement.spacedBy(verticalGap)) {
                                EditSchoolUserFieldColumn(
                                    label = "School",
                                    required = true
                                ) {
                                    SchoolUserSchoolPopup(
                                        selectedSchool = selectedSchool,
                                        selectedLabel = selectedSchoolLabel,
                                        query = schoolQuery,
                                        expanded = schoolExpanded,
                                        filteredSchools = filteredSchools,
                                        onQueryChange = { schoolQuery = it },
                                        onToggleExpanded = { schoolExpanded = !schoolExpanded },
                                        onSchoolSelected = {
                                            schoolId = it.id
                                            schoolName = it.name
                                            schoolQuery = ""
                                            schoolExpanded = false
                                        },
                                        onDismissExpanded = { schoolExpanded = false },
                                        error = if (hasSubmitted) validationErrors.schoolId else "",
                                        fieldTextColor = EditSchoolUserNavy,
                                        mutedTextColor = EditSchoolUserMuted,
                                        primaryColor = EditSchoolUserPrimary,
                                        borderColor = EditSchoolUserInputBorder,
                                        dangerColor = EditSchoolUserDanger,
                                        surfaceColor = EditSchoolUserSurface,
                                        highlightBorderColor = EditSchoolUserBorder
                                    )
                                }
                                EditSchoolUserFieldColumn(
                                    label = "Full Name",
                                    required = true
                                ) {
                                    EditSchoolUserTextField(
                                        value = fullName,
                                        onValueChange = { fullName = it },
                                        placeholder = "Enter full name",
                                        error = if (hasSubmitted) validationErrors.fullName else "",
                                        keyboardType = KeyboardType.Text
                                    )
                                }
                                EditSchoolUserFieldColumn(label = "Staff Role", required = true) {
                                    EditSchoolUserTextField(
                                        value = staffRole,
                                        onValueChange = { staffRole = it },
                                        placeholder = "Enter staff role",
                                        error = if (hasSubmitted) validationErrors.staffRole else "",
                                        keyboardType = KeyboardType.Text
                                    )
                                }
                                EditSchoolUserFieldColumn(
                                    label = "Mobile Number",
                                    required = true
                                ) {
                                    EditSchoolUserTextField(
                                        value = mobileNumber,
                                        onValueChange = { mobileNumber = it },
                                        placeholder = "7975782842",
                                        error = if (hasSubmitted) validationErrors.mobileNumber else "",
                                        keyboardType = KeyboardType.Phone
                                    )
                                }
                                EditSchoolUserFieldColumn(
                                    label = "Email Address",
                                    required = true
                                ) {
                                    EditSchoolUserTextField(
                                        value = emailAddress,
                                        onValueChange = { emailAddress = it },
                                        placeholder = "name@example.com",
                                        error = if (hasSubmitted) validationErrors.emailAddress else "",
                                        keyboardType = KeyboardType.Email
                                    )
                                }
                                EditSchoolUserFieldColumn(label = "Staff ID", required = true) {
                                    EditSchoolUserTextField(
                                        value = staffId,
                                        onValueChange = { staffId = it },
                                        placeholder = "Enter staff ID",
                                        error = if (hasSubmitted) validationErrors.staffId else "",
                                        keyboardType = KeyboardType.Text
                                    )
                                }
                                EditSchoolUserFieldColumn(
                                    label = "Role",
                                    required = true
                                ) {
                                    EditSchoolUserSelectField(
                                        value = role,
                                        expanded = roleExpanded,
                                        placeholder = "Select role",
                                        options = editSchoolUserRoles,
                                        onToggleExpanded = { roleExpanded = !roleExpanded },
                                        onDismissExpanded = { roleExpanded = false },
                                        onOptionSelected = {
                                            role = it
                                            roleExpanded = false
                                        },
                                        error = if (hasSubmitted) validationErrors.role else ""
                                    )
                                }
                                EditSchoolUserFieldColumn(
                                    label = "Gender",
                                    required = true
                                ) {
                                    EditSchoolUserSelectField(
                                        value = gender,
                                        expanded = genderExpanded,
                                        placeholder = "Select gender",
                                        options = genderOptions,
                                        onToggleExpanded = { genderExpanded = !genderExpanded },
                                        onDismissExpanded = { genderExpanded = false },
                                        onOptionSelected = {
                                            gender = it
                                            genderExpanded = false
                                        },
                                        error = if (hasSubmitted) validationErrors.gender else ""
                                    )
                                }
                                EditSchoolUserFieldColumn(
                                    label = "Status",
                                    required = true
                                ) {
                                    EditSchoolUserSelectField(
                                        value = status,
                                        expanded = statusExpanded,
                                        placeholder = "Active",
                                        options = editSchoolUserStatuses,
                                        onToggleExpanded = { statusExpanded = !statusExpanded },
                                        onDismissExpanded = { statusExpanded = false },
                                        onOptionSelected = {
                                            status = it
                                            statusExpanded = false
                                        },
                                        error = if (hasSubmitted) validationErrors.status else ""
                                    )
                                }
                            }
                        } else {
                            Column(verticalArrangement = Arrangement.spacedBy(verticalGap)) {
                                Row(horizontalArrangement = Arrangement.spacedBy(52.dp)) {
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "School", required = true) {
                                        SchoolUserSchoolPopup(
                                            selectedSchool = selectedSchool,
                                            selectedLabel = selectedSchoolLabel,
                                            query = schoolQuery,
                                            expanded = schoolExpanded,
                                            filteredSchools = filteredSchools,
                                            onQueryChange = { schoolQuery = it },
                                            onToggleExpanded = { schoolExpanded = !schoolExpanded },
                                            onSchoolSelected = {
                                                schoolId = it.id
                                                schoolName = it.name
                                                schoolQuery = ""
                                                schoolExpanded = false
                                            },
                                            onDismissExpanded = { schoolExpanded = false },
                                            error = if (hasSubmitted) validationErrors.schoolId else "",
                                            fieldTextColor = EditSchoolUserNavy,
                                            mutedTextColor = EditSchoolUserMuted,
                                            primaryColor = EditSchoolUserPrimary,
                                            borderColor = EditSchoolUserInputBorder,
                                            dangerColor = EditSchoolUserDanger,
                                            surfaceColor = EditSchoolUserSurface,
                                            highlightBorderColor = EditSchoolUserBorder
                                        )
                                    }
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "Full Name", required = true) {
                                        EditSchoolUserTextField(value = fullName, onValueChange = { fullName = it }, placeholder = "Enter full name", error = if (hasSubmitted) validationErrors.fullName else "", keyboardType = KeyboardType.Text)
                                    }
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(52.dp)) {
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "Staff Role", required = true) {
                                        EditSchoolUserTextField(value = staffRole, onValueChange = { staffRole = it }, placeholder = "Enter staff role", error = if (hasSubmitted) validationErrors.staffRole else "", keyboardType = KeyboardType.Text)
                                    }
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "Mobile Number", required = true) {
                                        EditSchoolUserTextField(value = mobileNumber, onValueChange = { mobileNumber = it }, placeholder = "7975782842", error = if (hasSubmitted) validationErrors.mobileNumber else "", keyboardType = KeyboardType.Phone)
                                    }
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(52.dp)) {
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "Email Address", required = true) {
                                        EditSchoolUserTextField(value = emailAddress, onValueChange = { emailAddress = it }, placeholder = "name@example.com", error = if (hasSubmitted) validationErrors.emailAddress else "", keyboardType = KeyboardType.Email)
                                    }
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "Staff ID", required = true) {
                                        EditSchoolUserTextField(value = staffId, onValueChange = { staffId = it }, placeholder = "Enter staff ID", error = if (hasSubmitted) validationErrors.staffId else "", keyboardType = KeyboardType.Text)
                                    }
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(52.dp)) {
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "Role", required = true) {
                                        EditSchoolUserSelectField(value = role, expanded = roleExpanded, placeholder = "Select role", options = editSchoolUserRoles, onToggleExpanded = { roleExpanded = !roleExpanded }, onDismissExpanded = { roleExpanded = false }, onOptionSelected = { role = it; roleExpanded = false }, error = if (hasSubmitted) validationErrors.role else "")
                                    }
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "Gender", required = true) {
                                        EditSchoolUserSelectField(value = gender, expanded = genderExpanded, placeholder = "Select gender", options = genderOptions, onToggleExpanded = { genderExpanded = !genderExpanded }, onDismissExpanded = { genderExpanded = false }, onOptionSelected = { gender = it; genderExpanded = false }, error = if (hasSubmitted) validationErrors.gender else "")
                                    }
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(52.dp)) {
                                    EditSchoolUserFieldColumn(modifier = Modifier.weight(1f), label = "Status", required = true) {
                                        EditSchoolUserSelectField(value = status, expanded = statusExpanded, placeholder = "Active", options = editSchoolUserStatuses, onToggleExpanded = { statusExpanded = !statusExpanded }, onDismissExpanded = { statusExpanded = false }, onOptionSelected = { status = it; statusExpanded = false }, error = if (hasSubmitted) validationErrors.status else "")
                                    }
                                    Spacer(modifier = Modifier.weight(1f))
                                }
                            }
                        }
                    }
                }
            }

            EditSchoolUserFooter(
                onClose = { if (!isSaving) onClose() },
                onSave = {
                    hasSubmitted = true
                    val values = currentValues
                    val errors = validateEditSchoolUser(values)
                    if (!errors.hasAnyError && canSave) {
                        onSave(values)
                    }
                },
                canSave = canSave,
                isSaving = isSaving
            )
        }

}

@Composable
private fun EditSchoolUserFooter(
    onClose: () -> Unit,
    onSave: () -> Unit,
    canSave: Boolean,
    isSaving: Boolean
) {
    AppDialogFooter(
        actions = listOf(
            AppDialogFooterAction(label = "Cancel", onClick = onClose, enabled = !isSaving),
            AppDialogFooterAction(
                label = if (isSaving) "Saving..." else "Save",
                onClick = onSave,
                style = AppDialogActionStyle.Primary,
                enabled = canSave
            )
        ),
        colors = EditSchoolUserDialogColors
    )
}

@Composable
private fun EditSchoolUserFieldColumn(
    modifier: Modifier = Modifier,
    label: String,
    required: Boolean,
    content: @Composable () -> Unit
) {
    AppDialogFieldColumn(
        modifier = modifier,
        label = label,
        required = required,
        colors = EditSchoolUserDialogColors,
        field = content
    )
}

@Composable
private fun EditSchoolUserTextField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    error: String,
    keyboardType: KeyboardType
) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        when {
            placeholder.equals("Enter full name", ignoreCase = true) -> AppDialogFullNameField(
                value = value,
                onValueChange = onValueChange,
                placeholder = placeholder,
                error = error,
                colors = EditSchoolUserDialogColors
            )
            keyboardType == KeyboardType.Phone -> AppDialogMobileField(
                value = value,
                onValueChange = onValueChange,
                placeholder = placeholder,
                error = error,
                colors = EditSchoolUserDialogColors
            )
            else -> AppDialogTextField(
                value = value,
                onValueChange = onValueChange,
                placeholder = placeholder,
                error = error,
                keyboardType = keyboardType,
                colors = EditSchoolUserDialogColors
            )
        }
        if (error.isNotBlank()) {
            Text(
                text = error,
                color = EditSchoolUserDanger,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
private fun EditSchoolUserSelectField(
    value: String,
    expanded: Boolean,
    placeholder: String,
    options: List<String> = emptyList(),
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onOptionSelected: (String) -> Unit,
    error: String
) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        AppDialogSelectField(
            value = value,
            placeholder = placeholder,
            expanded = expanded,
            options = options,
            onToggleExpanded = onToggleExpanded,
            onDismissExpanded = onDismissExpanded,
            onSelected = onOptionSelected,
            error = error,
            colors = EditSchoolUserDialogColors
        )
        if (error.isNotBlank()) {
            Text(
                text = error,
                color = EditSchoolUserDanger,
                fontSize = 13.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}

@Composable
private fun EditSchoolUserSchoolField(
    selectedSchool: AddSchoolUserSchoolOption?,
    selectedSchoolLabel: String,
    query: String,
    expanded: Boolean,
    filteredSchools: List<AddSchoolUserSchoolOption>,
    onQueryChange: (String) -> Unit,
    onToggleExpanded: () -> Unit,
    onSchoolSelected: (AddSchoolUserSchoolOption) -> Unit,
    error: String
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(40.dp)
                    .border(
                        1.dp,
                        if (expanded) EditSchoolUserPrimary else if (error.isNotBlank()) EditSchoolUserDanger else EditSchoolUserInputBorder,
                        RoundedCornerShape(8.dp)
                    )
                    .background(EditSchoolUserSurface, RoundedCornerShape(8.dp))
                    .clickable(onClick = onToggleExpanded)
                    .padding(horizontal = 12.dp),
                contentAlignment = Alignment.CenterStart
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        text = if (selectedSchoolLabel.isBlank()) "Search and select school" else selectedSchoolLabel,
                        color = if (selectedSchoolLabel.isBlank()) EditSchoolUserMuted else EditSchoolUserNavy,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.Medium,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f)
                    )
                    Icon(
                        painter = painterResource(Res.drawable.chevron_down),
                        contentDescription = null,
                        tint = EditSchoolUserPrimary,
                        modifier = Modifier
                            .size(20.dp)
                            .rotate(if (expanded) 180f else 0f)
                    )
                }
            }
            if (expanded) {
                Surface(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, EditSchoolUserBorder, RoundedCornerShape(8.dp)),
                    shape = RoundedCornerShape(8.dp),
                    color = EditSchoolUserSurface,
                    shadowElevation = 0.dp,
                    tonalElevation = 0.dp,
                    border = androidx.compose.foundation.BorderStroke(1.dp, EditSchoolUserBorder)
                ) {
                    Column {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(56.dp)
                                .padding(horizontal = 18.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.search),
                                contentDescription = null,
                                tint = EditSchoolUserMuted,
                                modifier = Modifier.size(22.dp)
                            )
                            Spacer(modifier = Modifier.size(10.dp))
                            BasicTextField(
                                value = query,
                                onValueChange = onQueryChange,
                                singleLine = true,
                                textStyle = TextStyle(
                                    fontSize = 14.sp,
                                    lineHeight = 18.sp,
                                    fontWeight = FontWeight.Medium,
                                    color = EditSchoolUserNavy
                                ),
                                cursorBrush = SolidColor(com.schoolconnect.app.designsystem.AppPrimary),
                                modifier = Modifier.fillMaxWidth()
                            ) { innerTextField ->
                                Box(
                                    modifier = Modifier.fillMaxWidth(),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    if (query.isBlank()) {
                                        Text(
                                            text = "Search school...",
                                            color = EditSchoolUserMuted,
                                            fontSize = 14.sp,
                                            fontWeight = FontWeight.Medium
                                        )
                                    }
                                    innerTextField()
                                }
                            }
                        }
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(max = 260.dp)
                                .verticalScroll(rememberScrollState())
                                .padding(vertical = 10.dp)
                        ) {
                            filteredSchools.forEach { school ->
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(36.dp)
                                        .clickable(onClick = { onSchoolSelected(school) })
                                        .padding(horizontal = 18.dp),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    Text(
                                        text = school.name,
                                        color = EditSchoolUserNavy,
                                        fontSize = 14.sp,
                                        fontWeight = FontWeight.Medium,
                                        maxLines = 1,
                                        overflow = TextOverflow.Ellipsis
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
        if (error.isNotBlank()) {
            Text(
                text = error,
                color = EditSchoolUserDanger,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium
            )
        }
    }
}
